import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Collapse, Pagination, Spin, Tag, Empty, message } from 'antd'
import { getPortfolioSignalDetail, getPortfolioSignals } from '@/api/portfolio'
import type { SignalItem } from '@/types/portfolio'
import { MdViewer } from '@/components/MarkdownFile'

const PAGE_SIZE = 15

const typeConfig: Record<string, { label: string; color: string }> = {
    REBALANCE: { label: '定期调仓', color: '#22c55e' },
    RISK_TRIGGER: { label: '风险监控调仓', color: '#eab308' },
    RISK_ALERT: { label: '风险预警', color: '#ef4444' },
}

const typeFilters = [
    { key: 'all',           label: '全部' },
    { key: 'REBALANCE',     label: '定期调仓' },
    { key: 'RISK_TRIGGER',  label: '风险监控调仓' },
    { key: 'RISK_ALERT',    label: '风险预警' },
    { key: 'executed',      label: '已触发调仓' },
    { key: 'pending',       label: '未触发' },
]

type ThinkingStep = {
    step: number
    tool: string
    args?: Record<string, unknown>
    result_preview?: string
}

type StockReport = {
    code: string
    recommendation: 'buy' | 'hold' | 'avoid'
    confidence: number
    thesis: string
    evidence?: string[]
    risks?: string[]
    invalidation_conditions?: string[]
    expected_holding_days?: number | null
}

type RiskReview = {
    approved: boolean
    severity: 'none' | 'low' | 'medium' | 'high' | 'critical'
    reasons?: string[]
    required_changes?: string[]
}

type PerFilterCount = {
    field?: string
    operator?: string
    value?: unknown
    matched?: number
    missing?: number
    invalid?: boolean
}

type ScreeningDetail = {
    screening_mode?: string
    filters_applied?: Array<{ field?: string; operator?: string; value?: unknown }>
    matched_count?: number
    returned_count?: number
    post_gate_count?: number
    eligible_candidate_count?: number
    eligible_candidate_codes?: string[]
    retained_holding_count?: number
    retained_holding_codes?: string[]
    excluded_after_match_count?: number
    total_universe?: number
    per_filter_counts?: PerFilterCount[]
    invalid_filters?: Array<{ field?: string; reason?: string }>
}

type DeterministicScore = {
    strategy_type?: string
    company_type?: string
    total_score?: number | null
    complete?: boolean
    data_completeness_pct?: number
    subscores?: Record<string, number | null>
    action_bucket?: string | null
    hard_excluded?: boolean
    hard_exclusion_reasons?: string[]
    new_buy_excluded?: boolean
    new_buy_exclusion_reasons?: string[]
    score_actionable?: boolean
    critical_missing_fields?: string[]
    hard_filter_missing_fields?: string[]
    report_stale?: boolean
    report_age_days?: number
    warnings?: string[]
}

type BuyableCandidateStats = {
    candidates?: number
    buyable?: number
    top_buyable_score?: number | null
    blocked?: Record<string, number>
}

const SUBSCORE_LABELS: Record<string, string> = {
    revenue_growth: '收入增长',
    gross_margin: '毛利率',
    growth_stability: '增长稳定性',
    profit_cashflow: '盈利/现金流',
    shareholder_return: '股东回报',
    cashflow_coverage: '现金流覆盖',
    dividend_stability: '分红稳定',
    balance_sheet: '资产负债',
    balance_sheet_quality: '资产负债质量',
    fundamental_stability: '基本面',
    valuation: '估值',
    cashflow_dividend_coverage: '现金流分红覆盖',
    valuation_margin_of_safety: '估值安全边际',
}

const BLOCK_REASON_LABELS: Record<string, string> = {
    below_score: '低于新买入分数',
    coverage: '数据完整度不足',
    hard_excluded: '硬性剔除',
    new_buy_excluded: '不满足新买入硬门槛',
    incomplete: '评分不完整',
    critical_missing: '关键字段缺失',
    hard_filter_missing: '硬筛字段缺失',
    report_stale: '财报陈旧',
}

const ACTION_BUCKET_LABELS: Record<string, string> = {
    sell: '低于卖出线',
    below_new_buy: '低于新买入门槛',
    hold_or_buy: '可持有/买入',
    observe: '观察',
}

const TOOL_LABELS: Record<string, string> = {
    get_current_portfolio: '获取当前持仓',
    get_universe:          '获取可投资标的',
    screen_stocks:         '筛选候选股票',
    query_fundamentals:    '查询基本面数据',
    analyze_candidates:    '深度研究候选股',
    submit_portfolio_decision: '提交目标组合提案',
}

const DECISION_OUTCOME_LABELS: Record<string, { label: string; color: string }> = {
    ACCEPTED: { label: '决策通过', color: '#22c55e' },
    NO_ACTION: { label: '无需操作', color: '#64748b' },
    FALLBACK_HOLD: { label: '降级维持', color: '#eab308' },
    DATA_INSUFFICIENT: { label: '数据不足', color: '#eab308' },
    RISK_REJECTED: { label: '风控拒绝', color: '#ef4444' },
    RISK_REVIEW_FAILED: { label: '风控复核失败', color: '#ef4444' },
    VALIDATION_FAILED: { label: '门禁未通过', color: '#ef4444' },
    EXECUTION_FAILED: { label: '下单失败', color: '#ef4444' },
    EXECUTION_PARTIAL_FAILED: { label: '部分下单失败', color: '#f97316' },
    AGENT_ERROR: { label: '决策失败', color: '#ef4444' },
}

const RECOMMEND_CONFIG = {
    buy:   { label: '买入', color: '#22c55e' },
    hold:  { label: '持有', color: '#eab308' },
    avoid: { label: '回避', color: '#ef4444' },
}

const SEVERITY_CONFIG: Record<string, { label: string; color: string }> = {
    none:     { label: '无', color: '#64748b' },
    low:      { label: '低', color: '#22c55e' },
    medium:   { label: '中', color: '#eab308' },
    high:     { label: '高', color: '#f97316' },
    critical: { label: '严重', color: '#ef4444' },
}

function signalTime(signal: SignalItem): number {
    if (signal.created_at) {
        const ts = Date.parse(signal.created_at)
        if (!Number.isNaN(ts)) return ts
    }
    const day = Date.parse(signal.date)
    if (!Number.isNaN(day)) return day
    return signal.id
}

function formatSignalTime(signal: SignalItem): string {
    if (signal.created_at) {
        const d = new Date(signal.created_at)
        if (!Number.isNaN(d.getTime())) {
            const pad = (n: number) => String(n).padStart(2, '0')
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
        }
    }
    return signal.date
}

function summaryPreview(signal: SignalItem): string {
    const text = (signal.ai_summary_preview || signal.ai_summary || signal.description || '')
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/^#+\s*/gm, '')
        .replace(/\s+/g, ' ')
        .trim()
    if (!text) {
        if (signal.orders?.length) return `目标仓位 ${signal.orders.length} 只`
        return signal.status_text || '暂无摘要'
    }
    return text.length > 120 ? `${text.slice(0, 120)}…` : text
}

function sortDesc(items: SignalItem[]): SignalItem[] {
    return [...items].sort((a, b) => {
        const diff = signalTime(b) - signalTime(a)
        return diff !== 0 ? diff : b.id - a.id
    })
}

type FilterRow = { field: string; operator: string; value: number | string }
type OrderRow2 = { code: string; target_weight: number; reason?: string }

type DecisionAdjustment = {
    code: string
    proposed: number
    final: number
}

function signalEventLabel(signal: SignalItem): string {
    const detail = signal.trigger_detail ?? {}
    const eventType = signal.event_type ?? detail.event_type as string | undefined
    const eventSource = signal.event_source ?? detail.event_source as string | undefined
    if (eventSource === 'initial_build') return '首次建仓'
    if (eventSource === 'scan' || eventSource === 'daily_scan') return '日常建仓扫描'
    if (eventSource === 'manual') return eventType || '手动完整调仓'
    if (eventType && eventType !== 'FULL') return eventType
    return typeConfig[signal.signal_type]?.label ?? signal.type ?? signal.signal_type
}

function targetWeightOf(orders: OrderRow2[]): number {
    return orders.reduce((total, order) => total + Number(order.target_weight || 0), 0)
}

function latestProposalOrders(steps: ThinkingStep[]): OrderRow2[] {
    const submissions = steps.filter(step => step.tool === 'submit_portfolio_decision')
    const raw = submissions[submissions.length - 1]?.args?.orders
    return Array.isArray(raw) ? raw as OrderRow2[] : []
}

function decisionAdjustments(proposal: OrderRow2[], finalOrders: OrderRow2[]): DecisionAdjustment[] {
    const proposed = new Map(proposal.map(order => [order.code, Number(order.target_weight || 0)]))
    const final = new Map(finalOrders.map(order => [order.code, Number(order.target_weight || 0)]))
    return [...new Set([...proposed.keys(), ...final.keys()])]
        .map(code => ({ code, proposed: proposed.get(code) ?? 0, final: final.get(code) ?? 0 }))
        .filter(item => Math.abs(item.proposed - item.final) > 1e-6)
}

function StepBody({ step, postGateCandidateCodes }: {
    step: ThinkingStep
    postGateCandidateCodes?: Set<string>
}) {
    const a = step.args ?? {}

    if (step.tool === 'screen_stocks') {
        const filters = a.filters as FilterRow[] | undefined
        let rows: Array<{ code?: string; is_holding?: boolean }> = []
        let previewMeta: Record<string, unknown> = {}
        if (step.result_preview) {
            try {
                const parsed = JSON.parse(step.result_preview)
                previewMeta = parsed ?? {}
                rows = (parsed?.results ?? []) as Array<{ code?: string; is_holding?: boolean }>
            } catch {
                // fallback: regex for legacy truncated data
                rows = [...step.result_preview.matchAll(/"code":\s*"([^"]+)"/g)]
                    .map(m => ({ code: m[1] }))
            }
        }
        const holdingCodes = rows.filter(row => row.is_holding === true).map(row => row.code).filter(Boolean) as string[]
        const candidateCodes = rows.filter(row => row.is_holding !== true).map(row => row.code).filter(Boolean) as string[]
        const hasHoldingFlag = rows.some(row => typeof row.is_holding === 'boolean')
        return (
            <div className="asset-thinking__step-body">
                {filters?.length ? (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">筛选条件</span>
                        <ul>{filters.filter(Boolean).map((f, i) => (
                            <li key={i}>
                                <code>{f.field ?? ''}</code>
                                {' '}{f.operator ?? ''}{' '}
                                <strong>{f.value != null ? String(f.value) : ''}</strong>
                            </li>
                        ))}</ul>
                    </div>
                ) : null}
                {(previewMeta.matched_count != null || previewMeta.returned_count != null) && (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">筛选漏斗</span>
                        <span>
                            硬筛命中 {String(previewMeta.matched_count ?? '--')} 只
                            {' → '}门禁后 {String(previewMeta.post_gate_count ?? previewMeta.returned_count ?? '--')} 只
                        </span>
                    </div>
                )}
                {candidateCodes.length > 0 && (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">
                            {hasHoldingFlag ? `可研究新候选（${candidateCodes.length} 只）` : `门禁后标的（${candidateCodes.length} 只）`}
                        </span>
                        <span>{candidateCodes.join(' · ')}</span>
                    </div>
                )}
                {holdingCodes.length > 0 && (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">同步复核持仓（{holdingCodes.length} 只）</span>
                        <span>{holdingCodes.join(' · ')}</span>
                    </div>
                )}
            </div>
        )
    }

    if (step.tool === 'query_fundamentals') {
        const codes = (a.queried_codes ?? a.codes) as string[] | undefined
        const fields = a.fields as string[] | undefined
        const KEY_FIELDS = ['revenue_yoy_pct', 'gross_margin_pct', 'operating_margin_pct', 'pe_ttm_percentile_3y', 'fcf_ttm', 'market_cap']
        const shown = fields ? KEY_FIELDS.filter(f => fields.includes(f)) : []
        const extra = fields ? fields.length - shown.length : 0
        return (
            <div className="asset-thinking__step-body">
                {codes?.length ? (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">查询标的</span>
                        <span>{codes.join(' · ')}</span>
                    </div>
                ) : null}
                {shown.length > 0 && (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">关键指标</span>
                        <span>{shown.join('、')}{extra > 0 ? `，等共 ${fields!.length} 项` : ''}</span>
                    </div>
                )}
            </div>
        )
    }

    if (step.tool === 'analyze_candidates') {
        const actualCandidateCodes = (a.candidate_codes ?? a.requested_codes ?? a.codes) as string[] | undefined
        const holdingCodes = a.holding_supplement_codes as string[] | undefined
        const rejectedCodes = a.rejected_requested_codes as string[] | undefined
        const total = a.unique_research_count as number | undefined
        const focus = a.focus as string | undefined
        const outOfPoolCodes = postGateCandidateCodes
            ? (actualCandidateCodes ?? []).filter(code => !postGateCandidateCodes.has(code))
            : []
        const candidateCodes = outOfPoolCodes.length > 0
            ? (actualCandidateCodes ?? []).filter(code => postGateCandidateCodes?.has(code))
            : actualCandidateCodes
        return (
            <div className="asset-thinking__step-body">
                {focus && (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">研究方向</span>
                        <span>{focus}</span>
                    </div>
                )}
                {total != null && (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">实际研究总数</span>
                        <span>
                            {total} 只（门禁后新候选 {candidateCodes?.length ?? 0} · 持仓复核 {holdingCodes?.length ?? 0}
                            {outOfPoolCodes.length > 0 ? ` · 历史越界研究 ${outOfPoolCodes.length}` : ''}）
                        </span>
                    </div>
                )}
                {candidateCodes?.length ? (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">新候选研究（{candidateCodes.length} 只）</span>
                        <span>{candidateCodes.join(' · ')}</span>
                    </div>
                ) : null}
                {holdingCodes?.length ? (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">持仓复核（{holdingCodes.length} 只）</span>
                        <span>{holdingCodes.join(' · ')}</span>
                    </div>
                ) : null}
                {rejectedCodes?.length ? (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">门禁拦截（{rejectedCodes.length} 只）</span>
                        <span>{rejectedCodes.join(' · ')}（未进入研究）</span>
                    </div>
                ) : null}
                {outOfPoolCodes.length > 0 && (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">历史越界研究（{outOfPoolCodes.length} 只）</span>
                        <span>{outOfPoolCodes.join(' · ')}（旧流程曾绕过门禁，仅保留为历史审计）</span>
                    </div>
                )}
                <div className="asset-thinking__step-note">↓ 详细评分见下方候选股研究</div>
            </div>
        )
    }

    if (step.tool === 'submit_portfolio_decision') {
        const orders = a.orders as OrderRow2[] | undefined
        let result: string | null = null
        if (step.result_preview) {
            try {
                const p = JSON.parse(step.result_preview)
                if (typeof p === 'string') result = p
            } catch { /* ignore */ }
        }
        return (
            <div className="asset-thinking__step-body">
                {result && <div className="asset-thinking__step-result">{result}</div>}
                {orders?.length ? (
                    <div className="asset-thinking__step-group">
                        <span className="asset-thinking__step-group-title">目标组合提案</span>
                        <ul>{orders.map((o, i) => (
                            <li key={i}>
                                <code>{o.code}</code>
                                <strong>{' → '}{(Number(o.target_weight) * 100).toFixed(2)}%</strong>
                                {o.reason && <em>{' '}{o.reason.length > 60 ? `${o.reason.slice(0, 60)}…` : o.reason}</em>}
                            </li>
                        ))}</ul>
                    </div>
                ) : null}
            </div>
        )
    }

    // fallback
    let fallbackText: string | null = null
    if (step.result_preview) {
        try {
            const p = JSON.parse(step.result_preview)
            if (typeof p === 'string') fallbackText = p
        } catch { /* ignore */ }
    }
    if (fallbackText) return <div className="asset-thinking__step-body">{fallbackText}</div>
    return null
}

function DecisionSteps({ steps }: { steps: ThinkingStep[] }) {
    if (!steps.length) return null
    let postGateCandidateCodes: Set<string> | undefined
    const screenStep = [...steps].reverse().find(step => step.tool === 'screen_stocks' && step.result_preview)
    if (screenStep?.result_preview) {
        try {
            const parsed = JSON.parse(screenStep.result_preview)
            postGateCandidateCodes = new Set(
                (parsed?.results ?? [])
                    .filter((row: { is_holding?: boolean }) => row.is_holding !== true)
                    .map((row: { code?: string }) => row.code)
                    .filter(Boolean),
            )
        } catch { /* legacy truncated preview: skip cross-step comparison */ }
    }
    return (
        <div className="asset-thinking__section">
            <div className="asset-thinking__section-title">决策步骤</div>
            <ol className="asset-thinking__steps">
                {steps.map(s => (
                    <li key={s.step} className="asset-thinking__step">
                        <span className="asset-thinking__step-label">
                            {TOOL_LABELS[s.tool] ?? s.tool}
                        </span>
                        <StepBody step={s} postGateCandidateCodes={postGateCandidateCodes} />
                    </li>
                ))}
            </ol>
        </div>
    )
}

function StockAnalysisSection({ reports }: { reports: StockReport[] }) {
    if (!reports.length) return null
    return (
        <div className="asset-thinking__section">
            <div className="asset-thinking__section-title">候选股研究（{reports.length} 只）</div>
            <Collapse
                size="small"
                className="asset-thinking__analysis"
                items={reports.map(r => {
                    const rec = RECOMMEND_CONFIG[r.recommendation] ?? { label: r.recommendation, color: '#64748b' }
                    const confidencePct = `${Math.round(r.confidence * 100)}%`
                    return {
                        key: r.code,
                        label: (
                            <span className="asset-thinking__analysis-header">
                                <span
                                    className="asset-thinking__analysis-rec"
                                    style={{ background: `${rec.color}20`, color: rec.color }}
                                >
                                    {rec.label}
                                </span>
                                <code className="asset-thinking__analysis-code">{r.code}</code>
                                <span className="asset-thinking__analysis-confidence">置信度 {confidencePct}</span>
                                {r.expected_holding_days != null && (
                                    <span className="asset-thinking__analysis-days">预计持有 {r.expected_holding_days} 天</span>
                                )}
                            </span>
                        ),
                        children: (
                            <div className="asset-thinking__analysis-body">
                                <p className="asset-thinking__analysis-thesis">{r.thesis}</p>
                                {r.evidence && r.evidence.length > 0 && (
                                    <div className="asset-thinking__analysis-list">
                                        <div className="asset-thinking__analysis-list-title">支撑论据</div>
                                        <ul>{r.evidence.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                    </div>
                                )}
                                {r.risks && r.risks.length > 0 && (
                                    <div className="asset-thinking__analysis-list">
                                        <div className="asset-thinking__analysis-list-title">主要风险</div>
                                        <ul>{r.risks.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                    </div>
                                )}
                                {r.invalidation_conditions && r.invalidation_conditions.length > 0 && (
                                    <div className="asset-thinking__analysis-list">
                                        <div className="asset-thinking__analysis-list-title">失效条件</div>
                                        <ul>{r.invalidation_conditions.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                    </div>
                                )}
                            </div>
                        ),
                    }
                })}
            />
        </div>
    )
}

function RiskReviewSection({ review }: { review: RiskReview }) {
    const sev = SEVERITY_CONFIG[review.severity] ?? { label: review.severity, color: '#64748b' }
    const verdict = review.approved
        ? { label: '审批通过', color: '#22c55e' }
        : { label: '审批否决', color: '#ef4444' }
    return (
        <div className="asset-thinking__section">
            <div className="asset-thinking__section-title">风险审批</div>
            <div className="asset-thinking__risk">
                <span
                    className="asset-thinking__risk-verdict"
                    style={{ background: `${verdict.color}20`, color: verdict.color }}
                >
                    {verdict.label}
                </span>
                <span
                    className="asset-thinking__risk-severity"
                    style={{ background: `${sev.color}20`, color: sev.color }}
                >
                    风险等级：{sev.label}
                </span>
            </div>
            {review.reasons && review.reasons.length > 0 && (
                <div className="asset-thinking__risk-list">
                    <div className="asset-thinking__risk-list-title">
                        {review.approved ? '审批意见' : '否决原因'}
                    </div>
                    <ul>{review.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </div>
            )}
            {!review.approved && review.required_changes && review.required_changes.length > 0 && (
                <div className="asset-thinking__risk-list">
                    <div className="asset-thinking__risk-list-title">要求修改</div>
                    <ul>{review.required_changes.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </div>
            )}
        </div>
    )
}

function ScreeningSection({ screening, scoredCount }: { screening: ScreeningDetail; scoredCount?: number }) {
    const hasFilters = (screening.per_filter_counts?.length ?? 0) > 0
    const hasInvalid = (screening.invalid_filters?.length ?? 0) > 0
    const hasMeta = screening.total_universe != null || screening.matched_count != null
    const postGate = screening.post_gate_count ?? screening.returned_count ?? 0
    // 新数据直接携带筛选漏斗；旧记录退回 matched/returned，再退回评分数量估算。
    const gatedOut = screening.excluded_after_match_count
        ?? (screening.matched_count != null && screening.matched_count > postGate
            ? screening.matched_count - postGate
            : scoredCount != null && scoredCount > postGate ? scoredCount - postGate : 0)
    if (!hasFilters && !hasInvalid && !hasMeta) return null
    return (
        <div className="asset-thinking__section">
            <div className="asset-thinking__section-title">筛选明细</div>
            {hasMeta && (
                <div className="asset-thinking__step-group">
                    <span className="asset-thinking__step-group-title">全市场</span>
                    <span>
                        {screening.total_universe ?? '--'} 只
                        {' → '}硬筛命中 {screening.matched_count ?? '--'} 只
                        {' → '}门禁后 {postGate} 只
                        {screening.screening_mode ? `（${screening.screening_mode}）` : ''}
                    </span>
                </div>
            )}
            {gatedOut > 0 && (
                <div className="asset-thinking__step-group">
                    <span className="asset-thinking__step-group-title">门禁剔除</span>
                    <span>
                        硬筛命中后另有 {gatedOut} 只因新买入门槛、数据完整度、财报时效或候选池上限未进入研究池
                    </span>
                </div>
            )}
            {(screening.eligible_candidate_count != null || screening.retained_holding_count != null) && (
                <div className="asset-thinking__step-group">
                    <span className="asset-thinking__step-group-title">门禁后构成</span>
                    <span>
                        可研究新候选 {screening.eligible_candidate_count ?? 0} 只
                        {' · '}硬筛内持仓 {screening.retained_holding_count ?? 0} 只
                    </span>
                </div>
            )}
            {hasFilters && (
                <div className="asset-thinking__step-group">
                    <span className="asset-thinking__step-group-title">逐条筛选命中数</span>
                    <ul>
                        {screening.per_filter_counts!.map((f, i) => (
                            <li key={i}>
                                <code>{f.field ?? ''}</code>
                                {' '}{f.operator ?? ''}{' '}
                                <strong>{f.value != null ? String(f.value) : ''}</strong>
                                <span>{' 命中 '}{f.matched ?? 0} 只</span>
                                {f.missing != null && (
                                    <span className="asset-thinking__filter-missing">{' 缺数据 '}{f.missing} 只</span>
                                )}
                                {f.invalid && (
                                    <span className="asset-thinking__filter-invalid">{' 无效字段'}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {hasInvalid && (
                <div className="asset-thinking__step-group">
                    <span className="asset-thinking__step-group-title">无效筛选</span>
                    <ul>
                        {screening.invalid_filters!.map((f, i) => (
                            <li key={i}>
                                <code>{f.field ?? ''}</code>
                                {f.reason ? `（${f.reason}）` : ''}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

function DeterministicScoreSection({ scores, minNewBuy }: { scores: Record<string, DeterministicScore>; minNewBuy?: number }) {
    const entries = Object.entries(scores ?? {})
    if (!entries.length) return null
    return (
        <div className="asset-thinking__section">
            <div className="asset-thinking__section-title">
                确定性评分{minNewBuy != null ? `（新买入门槛 ${Math.round(minNewBuy)} 分）` : ''}
            </div>
            <div className="asset-thinking__score-list">
                {entries.map(([code, s]) => {
                    const below = s.complete === true && minNewBuy != null && s.total_score != null && s.total_score < minNewBuy
                    const excluded = s.hard_excluded || s.new_buy_excluded
                    return (
                        <div key={code} className={`asset-thinking__score-item ${excluded ? 'is-excluded' : ''}`}>
                            <div className="asset-thinking__score-head">
                                <code>{code}</code>
                                <strong className={below ? 'is-below' : ''}>
                                    {s.total_score != null ? `${s.total_score} 分` : '-- 分'}
                                </strong>
                                {s.company_type && s.company_type !== 'normal' && (
                                    <span className="asset-thinking__score-bucket">{s.company_type}</span>
                                )}
                                {s.complete === false && <span className="asset-thinking__score-incomplete">数据不全</span>}
                                {s.hard_excluded && <span className="asset-thinking__score-excluded">硬性剔除</span>}
                                {s.new_buy_excluded && <span className="asset-thinking__score-excluded">禁止新建仓</span>}
                                {s.score_actionable === false && (
                                    <span className="asset-thinking__score-incomplete">评分仅供参考</span>
                                )}
                                {s.report_stale && <span className="asset-thinking__score-stale">财报陈旧</span>}
                                {s.hard_filter_missing_fields && s.hard_filter_missing_fields.length > 0 && (
                                    <span className="asset-thinking__score-missing-filter">缺数据过筛</span>
                                )}
                                {s.action_bucket && (
                                    <span className="asset-thinking__score-bucket">
                                        {ACTION_BUCKET_LABELS[s.action_bucket] ?? s.action_bucket}
                                    </span>
                                )}
                            </div>
                            {s.subscores && Object.keys(s.subscores).length > 0 && (
                                <div className="asset-thinking__score-subs">
                                    {Object.entries(s.subscores).map(([k, v]) => (
                                        <span key={k}>{SUBSCORE_LABELS[k] ?? k} {v != null ? v : '--'}</span>
                                    ))}
                                </div>
                            )}
                            {s.hard_exclusion_reasons && s.hard_exclusion_reasons.length > 0 && (
                                <div className="asset-thinking__score-reasons">
                                    剔除原因：{s.hard_exclusion_reasons.join('；')}
                                </div>
                            )}
                            {s.new_buy_exclusion_reasons && s.new_buy_exclusion_reasons.length > 0 && (
                                <div className="asset-thinking__score-reasons">
                                    新买入限制：{s.new_buy_exclusion_reasons.join('；')}
                                </div>
                            )}
                            {s.critical_missing_fields && s.critical_missing_fields.length > 0 && (
                                <div className="asset-thinking__score-missing-filter-detail">
                                    关键字段缺失：{s.critical_missing_fields.join('、')}
                                </div>
                            )}
                            {s.hard_filter_missing_fields && s.hard_filter_missing_fields.length > 0 && (
                                <div className="asset-thinking__score-missing-filter-detail">
                                    缺数据过筛字段：{s.hard_filter_missing_fields.join('、')}
                                </div>
                            )}
                            {s.warnings && s.warnings.length > 0 && (
                                <div className="asset-thinking__score-warnings">
                                    提示：{s.warnings.join('；')}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

function ReportStaleSection({ codes }: { codes: string[] }) {
    if (!codes?.length) return null
    return (
        <div className="asset-thinking__section" style={{ borderLeftColor: 'rgba(245, 158, 11, 0.5)' }}>
            <div className="asset-thinking__section-title">财报陈旧剔除</div>
            <div className="asset-thinking__step-group">
                <span className="asset-thinking__step-group-title">剔除标的（{codes.length} 只）</span>
                <span>{codes.join(' · ')}</span>
            </div>
        </div>
    )
}


function DataInsufficientSection({ codes }: { codes: string[] }) {
    if (!codes?.length) return null
    return (
        <div className="asset-thinking__section" style={{ borderLeftColor: 'rgba(239, 68, 68, 0.5)' }}>
            <div className="asset-thinking__section-title">数据完整度不足剔除</div>
            <div className="asset-thinking__step-group">
                <span className="asset-thinking__step-group-title">剔除标的（{codes.length} 只）</span>
                <span>{codes.join(' · ')}</span>
            </div>
            <div className="asset-thinking__step-group">
                <span>这些标的未通过策略的数据完整度门槛，已排除在候选池与个股研究之外，不得新建仓；数据补全后可重新评估。</span>
            </div>
        </div>
    )
}

function NewBuyIneligibleSection({ codes }: { codes: string[] }) {
    if (!codes?.length) return null
    return (
        <div className="asset-thinking__section" style={{ borderLeftColor: 'rgba(100, 116, 139, 0.5)' }}>
            <div className="asset-thinking__section-title">新建仓资格剔除</div>
            <div className="asset-thinking__step-group">
                <span className="asset-thinking__step-group-title">未进入研究池（{codes.length} 只）</span>
                <span>{codes.join(' · ')}</span>
            </div>
            <div className="asset-thinking__step-group">
                <span>这些标的通过了初始硬筛，但未通过分数线或策略的新买入附加门槛，仅保留评分审计。</span>
            </div>
        </div>
    )
}


function DecisionOverview({ signal, steps, finalOrders }: {
    signal: SignalItem
    steps: ThinkingStep[]
    finalOrders: OrderRow2[]
}) {
    const proposal = latestProposalOrders(steps)
    const proposedOrders = proposal.length ? proposal : finalOrders
    const adjustments = decisionAdjustments(proposedOrders, finalOrders)
    const detail = signal.trigger_detail ?? {}
    const outcomeCode = signal.decision_outcome ?? detail.decision_outcome as string | undefined
    const outcome = DECISION_OUTCOME_LABELS[outcomeCode ?? '']
        ?? { label: outcomeCode || signal.status_text || '状态未知', color: '#64748b' }
    const finalCount = finalOrders.filter(order => Number(order.target_weight) > 1e-9).length

    return (
        <div className="asset-thinking__decision-overview">
            <div className="asset-thinking__decision-overview-head">
                <div>
                    <strong>{signalEventLabel(signal)}</strong>
                    <span>{formatSignalTime(signal)}</span>
                </div>
                <div>
                    <Tag color={outcome.color}>{outcome.label}</Tag>
                    <Tag color={signal.is_executed ? 'processing' : 'default'}>
                        {signal.is_executed ? '已提交模拟交易' : '未产生成交'}
                    </Tag>
                </div>
            </div>
            <div className="asset-thinking__decision-stages">
                <div>
                    <span>1 · AI原始提案</span>
                    <strong>{proposedOrders.filter(order => Number(order.target_weight) > 1e-9).length} 只</strong>
                    <small>股票权重 {(targetWeightOf(proposedOrders) * 100).toFixed(2)}%</small>
                </div>
                <div>
                    <span>2 · 系统门禁后目标</span>
                    <strong>{finalCount} 只</strong>
                    <small>
                        股票权重 {(targetWeightOf(finalOrders) * 100).toFixed(2)}%
                        {adjustments.length ? ` · 调整 ${adjustments.length} 只` : ' · 未调整'}
                    </small>
                </div>
                <div>
                    <span>3 · 模拟盘执行</span>
                    <strong>{signal.is_executed ? '已执行' : '未执行'}</strong>
                    <small>{signal.trade || '没有产生可执行订单'}</small>
                </div>
            </div>
            {adjustments.length > 0 && (
                <div className="asset-thinking__decision-adjustments">
                    <span>系统门禁调整</span>
                    {adjustments.map(item => (
                        <Tag key={item.code}>
                            {item.code} {(item.proposed * 100).toFixed(2)}% → {(item.final * 100).toFixed(2)}%
                        </Tag>
                    ))}
                </div>
            )}
            <p className="asset-thinking__decision-note">
                目标组合不是成交订单；是否真实提交模拟交易以执行状态和交易记录为准。
            </p>
        </div>
    )
}


function ThinkingDetailBody({ signal }: { signal: SignalItem }) {
    const cfg = typeConfig[signal.signal_type]
        ?? { label: signal.type || signal.signal_type, color: '#64748b' }
    const orders = (signal.orders ?? []) as OrderRow2[]
    const steps = (signal.thinking_chain ?? []) as ThinkingStep[]
    const reports = (signal.stock_analysis ?? []) as StockReport[]
    const triggerDetail = signal.trigger_detail as Record<string, unknown> | undefined
    const riskReview = triggerDetail?.risk_review as RiskReview | undefined
    const screening = triggerDetail?.screening as ScreeningDetail | undefined
    const detScores = triggerDetail?.deterministic_scores as Record<string, DeterministicScore> | undefined
    const minNewBuy = triggerDetail?.min_new_buy_score as number | undefined
    const drift = triggerDetail?.scoring_model_drift as string[] | undefined
    const staleCodes = (triggerDetail?.report_stale_codes as string[] | undefined) ?? []
    const insufficientCodes = (triggerDetail?.data_insufficient_codes as string[] | undefined) ?? []
    const ineligibleCodes = (triggerDetail?.new_buy_ineligible_codes as string[] | undefined) ?? []
    const candidateStats = triggerDetail?.buyable_candidate_stats as BuyableCandidateStats | undefined
    const scanSkipped = triggerDetail?.daily_scan_skipped as boolean | undefined
    const scanSkipReason = triggerDetail?.daily_scan_skip_reason as string | undefined
    const auditItems = [
        steps.length > 0 ? {
            key: 'steps',
            label: `AI 工具过程（${steps.length} 步）`,
            children: <DecisionSteps steps={steps} />,
        } : null,
        screening ? {
            key: 'screening',
            label: '完整筛选明细',
            children: (
                <ScreeningSection
                    screening={screening}
                    scoredCount={detScores ? Object.keys(detScores).length : undefined}
                />
            ),
        } : null,
        detScores ? {
            key: 'scores',
            label: `全部确定性评分（${Object.keys(detScores).length} 只）`,
            children: <DeterministicScoreSection scores={detScores} minNewBuy={minNewBuy} />,
        } : null,
        candidateStats ? {
            key: 'candidate-gate',
            label: '新买入门禁',
            children: (
                <CandidateGateSection
                    stats={candidateStats}
                    skipped={scanSkipped}
                    reason={scanSkipReason}
                />
            ),
        } : null,
        reports.length > 0 ? {
            key: 'reports',
            label: `全部候选股研究（${reports.length} 只）`,
            children: <StockAnalysisSection reports={reports} />,
        } : null,
        riskReview ? {
            key: 'risk',
            label: '风险审批记录',
            children: <RiskReviewSection review={riskReview} />,
        } : null,
        orders.length > 0 ? {
            key: 'orders',
            label: `系统门禁后目标仓位（${orders.length} 条）`,
            children: (
                <div className="asset-thinking__item-orders">
                    <ul>
                        {orders.map((order, index) => (
                            <li key={`${order.code ?? 'x'}-${index}`}>
                                <code>{order.code || '--'}</code>
                                <span>{(Number(order.target_weight || 0) * 100).toFixed(1)}%</span>
                                {order.reason ? <em>{order.reason}</em> : null}
                            </li>
                        ))}
                    </ul>
                </div>
            ),
        } : null,
    ].filter(Boolean) as Array<{ key: string; label: string; children: ReactNode }>

    return (
        <div className="asset-thinking__item-body">
            <DecisionOverview signal={signal} steps={steps} finalOrders={orders} />

            {(signal.description || signal.ai_summary) && (
                <Collapse
                    className="asset-thinking__decision-detail-collapse"
                    defaultActiveKey={['summary']}
                    items={[{
                        key: 'summary',
                        label: 'AI 决策说明（原始记录）',
                        children: <MdViewer content={signal.description || signal.ai_summary || ''} />,
                    }]}
                />
            )}

            {staleCodes.length > 0 && <ReportStaleSection codes={staleCodes} />}
            {insufficientCodes.length > 0 && <DataInsufficientSection codes={insufficientCodes} />}
            {ineligibleCodes.length > 0 && <NewBuyIneligibleSection codes={ineligibleCodes} />}

            {drift && drift.length > 0 && (
                <div className="asset-thinking__section" style={{ borderLeftColor: 'rgba(239, 68, 68, 0.4)' }}>
                    <div className="asset-thinking__section-title">评分执行参数漂移告警</div>
                    <div className="asset-thinking__step-group">
                        <span>分数线、仓位或换手等执行参数与审核规则不一致；系统已回退确定性规则：</span>
                        <ul>{drift.map((item, index) => <li key={index}>{item}</li>)}</ul>
                    </div>
                </div>
            )}

            {auditItems.length > 0 && (
                <Collapse
                    className="asset-thinking__decision-detail-collapse"
                    items={auditItems}
                />
            )}

            {signal.tags?.length > 0 && (
                <div className="asset-thinking__item-tags">
                    {signal.tags.map(tag => (
                        <Tag
                            key={tag}
                            style={{ fontSize: 11, borderColor: `${cfg.color}40`, color: cfg.color }}
                        >
                            {tag}
                        </Tag>
                    ))}
                </div>
            )}

            {signal.trade && (
                <div className="asset-thinking__item-trade">
                    <span className="asset-thinking__item-trade-label">执行结果</span>
                    <span className="asset-thinking__item-trade-val">{signal.trade}</span>
                </div>
            )}
        </div>
    )
}


function CandidateGateSection({
    stats,
    skipped,
    reason,
}: {
    stats: BuyableCandidateStats
    skipped?: boolean
    reason?: string
}) {
    const blocked = Object.entries(stats.blocked ?? {}).filter(([, count]) => Number(count) > 0)
    return (
        <div className="asset-thinking__section">
            <div className="asset-thinking__section-title">新买入门禁</div>
            <div className="asset-thinking__gate-metrics">
                <span>候选 <strong>{stats.candidates ?? 0}</strong> 只</span>
                <span>可买入 <strong>{stats.buyable ?? 0}</strong> 只</span>
                <span>
                    最高可买分数 <strong>{stats.top_buyable_score ?? '--'}</strong>
                </span>
                {skipped && <Tag color="warning">已跳过 AI 复评</Tag>}
            </div>
            {reason && <div className="asset-thinking__gate-reason">{reason}</div>}
            {blocked.length > 0 && (
                <div className="asset-thinking__gate-blocked">
                    {blocked.map(([key, count]) => (
                        <Tag key={key}>{BLOCK_REASON_LABELS[key] ?? key} {count} 只</Tag>
                    ))}
                </div>
            )}
        </div>
    )
}


function ThinkingItem({ portfolioId, signal }: { portfolioId: number; signal: SignalItem }) {
    const [detail, setDetail] = useState<SignalItem | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const cfg = typeConfig[signal.signal_type]
        ?? { label: signal.type || signal.signal_type, color: '#64748b' }
    const outcomeCode = signal.decision_outcome
    const outcome = DECISION_OUTCOME_LABELS[outcomeCode ?? '']
        ?? { label: outcomeCode || signal.status_text, color: '#64748b' }

    const loadDetail = async () => {
        if (detail || detailLoading) return
        setDetailLoading(true)
        try {
            const response = await getPortfolioSignalDetail(portfolioId, signal.id)
            setDetail(response.item)
        } catch {
            message.error('加载 AI 决策详情失败')
        } finally {
            setDetailLoading(false)
        }
    }

    return (
        <Collapse
            className="asset-thinking__item"
            items={[{
                key: String(signal.id),
                label: (
                    <div className="asset-thinking__item-header-wrap">
                        <div className="asset-thinking__item-header">
                            <span className="asset-thinking__item-date">{formatSignalTime(signal)}</span>
                            <span
                                className="asset-thinking__item-badge"
                                style={{ background: `${cfg.color}20`, color: cfg.color }}
                            >
                                {signalEventLabel(signal)}
                            </span>
                            {outcome.label && (
                                <span
                                    className="asset-thinking__item-badge"
                                    style={{ background: `${outcome.color}20`, color: outcome.color }}
                                >
                                    {outcome.label}
                                </span>
                            )}
                            <span className="asset-thinking__item-title">{signal.title}</span>
                            {signal.status_text && (
                                <span className="asset-thinking__item-status">{signal.status_text}</span>
                            )}
                            <span className={`asset-thinking__item-exec ${signal.is_executed ? 'is-executed' : 'is-pending'}`}>
                                {signal.is_executed ? '已触发调仓' : '未触发'}
                            </span>
                        </div>
                        <div className="asset-thinking__item-facts">
                            <span>目标持仓 {signal.target_position_count ?? '--'} 只</span>
                            {signal.target_weight != null && (
                                <span>股票权重 {(signal.target_weight * 100).toFixed(2)}%</span>
                            )}
                            <span>{signal.is_executed ? '已提交模拟交易' : '未产生成交'}</span>
                        </div>
                        <p className="asset-thinking__item-preview">
                            {signal.ai_summary_preview
                                ? summaryPreview(signal)
                                : signal.has_full_summary
                                    ? '点击展开查看完整决策链'
                                    : summaryPreview(signal)}
                        </p>
                    </div>
                ),
                children: detailLoading ? (
                    <div className="asset-thinking__detail-loading"><Spin size="small" /></div>
                ) : detail ? (
                    <ThinkingDetailBody signal={detail} />
                ) : null,
            }]}
            onChange={keys => {
                if (keys.length > 0) void loadDetail()
            }}
        />
    )
}

export default function AIThinkingLog({ portfolioId }: { portfolioId: number }) {
    const [items, setItems] = useState<SignalItem[]>([])
    const [loading, setLoading] = useState(true)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [filter, setFilter] = useState('all')

    const fetchPage = (p: number) => {
        setLoading(true)
        getPortfolioSignals(portfolioId, PAGE_SIZE, (p - 1) * PAGE_SIZE, false)
            .then(res => {
                setTotal(res.total)
                setItems(sortDesc(res.items ?? []))
            })
            .catch(() => {
                setItems([])
                setTotal(0)
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        // 组合切换是分页边界，必须同步复位到第一页。
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPage(1)
        fetchPage(1)
        // fetchPage 只依赖当前 portfolioId；切换组合时重新创建并执行。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolioId])

    const filtered = useMemo(() => {
        const list = filter === 'all' ? items
            : filter === 'executed' ? items.filter(s => s.is_executed)
            : filter === 'pending'  ? items.filter(s => !s.is_executed)
            : items.filter(s => (s.signal_type || '').toUpperCase() === filter)
        return sortDesc(list)
    }, [items, filter])

    const handlePageChange = (p: number) => {
        setPage(p)
        fetchPage(p)
    }

    return (
        <div className="asset-thinking">
            <div className="asset-thinking__filters">
                {typeFilters.map(f => (
                    <button
                        key={f.key}
                        className={`asset-thinking__filter-btn ${filter === f.key ? 'active' : ''}`}
                        onClick={() => setFilter(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                    <Spin />
                </div>
            ) : filtered.length === 0 ? (
                <Empty style={{ marginTop: 64 }} description="暂无思考记录" />
            ) : (
                <>
                    <div className="asset-thinking__list">
                        {filtered.map(s => (
                            <ThinkingItem key={`${portfolioId}-${s.id}`} portfolioId={portfolioId} signal={s} />
                        ))}
                    </div>
                    {total > PAGE_SIZE && (
                        <div className="asset-thinking__pagination">
                            <Pagination
                                size="small"
                                current={page}
                                pageSize={PAGE_SIZE}
                                total={total}
                                showSizeChanger={false}
                                showTotal={t => `共 ${t} 条`}
                                onChange={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
